import type { Locale } from "@engine/generate/conceptCopy.js";
import { getPwaUi } from "../i18n/pwaUi.js";
import { requestEmailCode, verifyEmailCode } from "./session.js";

export function mountSignInModal(
  root: HTMLElement,
  locale: Locale,
  onClose: () => void,
  initialError?: string
): () => void {
  const ui = getPwaUi(locale);
  const overlay = document.createElement("div");
  overlay.className = "auth-overlay";
  overlay.innerHTML = `
    <div class="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title">
      <h2 id="auth-title" class="auth-title">${ui.authModalTitle}</h2>
      <form class="auth-form">
        <label class="auth-label">
          ${ui.authModalEmailLabel}
          <input type="email" class="auth-email" autocomplete="email" required />
        </label>
        <div class="auth-error" hidden></div>
        <div class="auth-actions">
          <button type="button" class="auth-cancel">${ui.authModalCancel}</button>
          <button type="submit" class="auth-submit">${ui.authModalSendButton}</button>
        </div>
      </form>
      <div class="auth-sent" hidden>
        <h3>${ui.authModalSentTitle}</h3>
        <p class="auth-sent-body"></p>
        <form class="auth-verify-form">
          <label class="auth-label">
            ${ui.authModalCodeLabel}
            <input
              type="text"
              class="auth-email auth-code"
              autocomplete="one-time-code"
              inputmode="numeric"
              required
            />
          </label>
          <div class="auth-actions">
            <button type="submit" class="auth-submit auth-verify-submit">${ui.authModalVerifyButton}</button>
          </div>
        </form>
      </div>
    </div>
  `;
  root.appendChild(overlay);

  const form = overlay.querySelector(".auth-form") as HTMLFormElement;
  const emailInput = overlay.querySelector(".auth-email") as HTMLInputElement;
  const errorEl = overlay.querySelector(".auth-error") as HTMLDivElement;
  const sentEl = overlay.querySelector(".auth-sent") as HTMLDivElement;
  const sentBody = overlay.querySelector(".auth-sent-body") as HTMLParagraphElement;
  const verifyForm = overlay.querySelector(".auth-verify-form") as HTMLFormElement;
  const codeInput = overlay.querySelector(".auth-code") as HTMLInputElement;
  const verifySubmit = overlay.querySelector(".auth-verify-submit") as HTMLButtonElement;
  const cancel = overlay.querySelector(".auth-cancel") as HTMLButtonElement;
  const submit = overlay.querySelector(".auth-submit") as HTMLButtonElement;
  let pendingEmail: string | null = null;

  if (initialError) {
    errorEl.textContent = initialError;
    errorEl.hidden = false;
  }

  const escClose = (e: KeyboardEvent): void => {
    if (e.key === "Escape") close();
  };

  const close = (): void => {
    document.removeEventListener("keydown", escClose);
    overlay.remove();
    onClose();
  };

  cancel.addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  document.addEventListener("keydown", escClose);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.hidden = true;
    const email = emailInput.value.trim();
    if (!email) return;
    submit.disabled = true;
    const r = await requestEmailCode(email);
    submit.disabled = false;
    if (r.ok) {
      pendingEmail = email;
      form.hidden = true;
      sentEl.hidden = false;
      sentBody.textContent = ui.authModalSentBody.replace("{email}", email);
      codeInput.focus();
    } else {
      errorEl.textContent = ui.authModalErrorPrefix + r.error;
      errorEl.hidden = false;
    }
  });

  verifyForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.hidden = true;
    const code = codeInput.value.trim();
    if (!pendingEmail || !code) return;
    verifySubmit.disabled = true;
    const r = await verifyEmailCode(pendingEmail, code);
    verifySubmit.disabled = false;
    if (r.ok) {
      close();
    } else {
      errorEl.textContent = ui.authModalErrorPrefix + r.error;
      errorEl.hidden = false;
    }
  });

  emailInput.focus();
  return close;
}
